import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { AlertTriangle, Camera } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { formatKilos } from '@/lib/utils'
import type { OperationalAlerts } from '@/types'

interface Props {
  data: OperationalAlerts | undefined
  isLoading: boolean
}

function formatDate(raw: string) {
  try {
    return format(new Date(raw), "d MMM yyyy", { locale: es })
  } catch {
    return raw
  }
}

export function OperationalAlertsPanel({ data, isLoading }: Props) {
  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-md bg-gray-100" />
  }

  const withoutDelivery = data?.pickups_without_delivery
  const withoutPhoto = data?.pickups_without_photo

  const noAlerts =
    (withoutDelivery?.count ?? 0) === 0 && (withoutPhoto?.count ?? 0) === 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Panel operacional</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {noAlerts && (
          <p className="text-sm text-muted-foreground">Sin alertas en el período seleccionado.</p>
        )}

        {(withoutDelivery?.count ?? 0) > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium">Retiros sin entrega a destino</span>
              <Badge variant="outline" className="ml-auto">
                {withoutDelivery!.count}
              </Badge>
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {withoutDelivery!.items.slice(0, 5).map((item) => (
                <li key={item.pickup_id} className="flex justify-between">
                  <span>Retiro #{item.pickup_id} — {formatDate(item.started_at)}</span>
                  {item.pending_kg !== undefined && (
                    <span className="font-medium">{formatKilos(item.pending_kg)} pendientes</span>
                  )}
                </li>
              ))}
              {withoutDelivery!.count > 5 && (
                <li className="text-muted-foreground">
                  … y {withoutDelivery!.count - 5} más
                </li>
              )}
            </ul>
          </div>
        )}

        {(withoutPhoto?.count ?? 0) > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Camera className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium">Retiros sin foto registrada</span>
              <Badge variant="outline" className="ml-auto">
                {withoutPhoto!.count}
              </Badge>
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {withoutPhoto!.items.slice(0, 5).map((item) => (
                <li key={item.pickup_id}>
                  Retiro #{item.pickup_id} — {formatDate(item.started_at)}
                </li>
              ))}
              {withoutPhoto!.count > 5 && (
                <li className="text-muted-foreground">
                  … y {withoutPhoto!.count - 5} más
                </li>
              )}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
